'use client'

import { useTranslations } from 'next-intl'
import {
  usePathname,
  useRouter,
  useSearchParams,
  useSelectedLayoutSegment,
} from 'next/navigation'
import { FC, useEffect, useMemo, useState, useTransition } from 'react'

import { Game } from '@/entities/games'
import { useDebounce } from '@/shared/libs'
import { InputField, Select } from '@/shared/ui'

export type RatingFiltersParams = {
  name?: string
  gameId?: GameId
}

interface RatingFiltersProps {
  games: Game[]
}

export const RatingFilters: FC<RatingFiltersProps> = ({ games }) => {
  const t = useTranslations('rating.filters')

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const segment = useSelectedLayoutSegment()
  const [isPending, startTransition] = useTransition()

  const [searchValue, setSearchValue] = useState(searchParams.get('name') ?? '')
  const debouncedSearch = useDebounce(searchValue)

  const navigate = (params: URLSearchParams) => {
    startTransition(() => {
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    })
  }

  useEffect(() => {
    const urlSearch = searchParams.get('name') ?? ''

    if (!urlSearch) {
      setSearchValue(urlSearch)
    }
  }, [searchParams])

  useEffect(() => {
    const currentUrlSearch = searchParams.get('name') ?? ''

    if (debouncedSearch !== currentUrlSearch) {
      updateSearchParams('name', debouncedSearch)
    }
  }, [debouncedSearch])

  const updateSearchParams = (name: keyof RatingFiltersParams, value: any) => {
    const params = new URLSearchParams(searchParams.toString())

    if (value && value !== 'all') {
      params.set(name, String(value))
    } else {
      params.delete(name)
    }

    navigate(params)
  }

  const gamesOptions = useMemo(
    () => [
      { value: 'all', label: t('allGames') },
      ...games.map((g) => ({ value: String(g.id), label: g.name })),
    ],
    [games],
  )

  return (
    <div
      aria-label={t('ariaLabel')}
      className={`flex flex-col sm:flex-row items-center gap-2.5 sm:gap-4 w-full 
        ${isPending ? 'opacity-80 pointer-events-none animate-pulse' : ''}`}
    >
      <InputField
        placeholder={
          segment === 'teams'
            ? t('placeholders.searchTeam')
            : t('placeholders.searchName')
        }
        title={
          segment === 'teams'
            ? t('placeholders.searchTeam')
            : t('placeholders.searchName')
        }
        className="sm:max-w-[250px]"
        iconRightSrc="/icons/search.svg"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />

      <Select
        options={gamesOptions}
        className="sm:max-w-[230px]"
        placeholder={t('placeholders.selectGame')}
        value={searchParams.get('gameId') ?? 'all'}
        onChange={(val) => updateSearchParams('gameId', val)}
      />
    </div>
  )
}
